import { useState } from "react";
import { Loader2, Plus, X, Save, Trash2, Megaphone, Mail, List, FileText, Send } from "lucide-react";
import {
  useCampanas,
  useCreateCampana,
  useDeleteCampana,
  useEnviarCampana,
  useGanadorAB,
  useListas,
  useCreateLista,
  usePlantillas,
  useCreatePlantilla,
  useDeletePlantilla,
} from "../api";
import { useMarketingStore } from "../store";
import { MarketingHeader } from "../components/MarketingHeader";
import type { Campana, CampanaFormData } from "../types";

function eur(n: number, compact = false): string {
  if (compact && Math.abs(n) >= 1000) {
    return new Intl.NumberFormat("es-ES", { notation: "compact", maximumFractionDigits: 1 }).format(n) + " €";
  }
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);
}

const CANALES: Record<string, string> = {
  email: "Email",
  sms: "SMS",
  social: "Social",
  mixta: "Mixta",
};

const ESTADO_CLS: Record<string, string> = {
  borrador: "bg-slate-100 text-slate-700 border-slate-200",
  programada: "bg-sky-50 text-sky-700 border-sky-200",
  enviando: "bg-amber-50 text-amber-700 border-amber-200",
  enviada: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pausada: "bg-rose-50 text-rose-700 border-rose-200",
};

type SubTab = "campanas" | "plantillas" | "listas";

export function CampanasPage() {
  const { emisorId } = useMarketingStore();
  const [tab, setTab] = useState<SubTab>("campanas");

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <MarketingHeader title="Marketing" subtitle="Campañas, plantillas de email y listas." />

      <div className="flex gap-2 shrink-0">
        {([
          ["campanas", "Campañas", Megaphone],
          ["plantillas", "Plantillas", FileText],
          ["listas", "Listas", List],
        ] as const).map(([k, label, Icon]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${
              tab === k
                ? "bg-brand-700 text-white shadow-soft"
                : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        {tab === "campanas" && <CampanasTab emisorId={emisorId} />}
        {tab === "plantillas" && <PlantillasTab emisorId={emisorId} />}
        {tab === "listas" && <ListasTab emisorId={emisorId} />}
      </div>
    </div>
  );
}

function CampanasTab({ emisorId }: { emisorId: number | null }) {
  const { data: campanas, isLoading } = useCampanas(emisorId);
  const { data: listas } = useListas(emisorId);
  const { data: plantillas } = usePlantillas(emisorId);
  const createMut = useCreateCampana();
  const deleteMut = useDeleteCampana();
  const enviarMut = useEnviarCampana();
  const abMut = useGanadorAB();
  const [creating, setCreating] = useState(false);
  const [enviando, setEnviando] = useState<number | null>(null);
  const [form, setForm] = useState<CampanaFormData>({
    nombre: "",
    canal: "email",
    estado: "borrador",
    presupuesto: 0,
  });

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!emisorId) return;
    await createMut.mutateAsync({ emisorId, payload: form });
    setCreating(false);
    setForm({ nombre: "", canal: "email", estado: "borrador", presupuesto: 0 });
  }

  async function enviar(id: number, nombre: string) {
    if (!confirm(`¿Enviar la campaña "${nombre}" a su lista de destinatarios?`)) return;
    setEnviando(id);
    try {
      const r = await enviarMut.mutateAsync(id);
      alert(`Campaña enviada: ${r.enviados} emails${r.errores ? `, ${r.errores} errores` : ""}.`);
    } catch (err: unknown) {
      const e2 = err as { response?: { data?: { error?: string } } };
      alert(e2?.response?.data?.error ?? "No se pudo enviar.");
    } finally {
      setEnviando(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => setCreating(true)}
          disabled={!emisorId}
          className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 font-bold text-sm shadow-soft disabled:opacity-50"
        >
          <Plus size={16} /> Nueva campaña
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      )}

      {campanas && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {campanas.length === 0 && (
            <div className="col-span-full text-center text-slate-500 py-10">
              Aún no hay campañas.
            </div>
          )}
          {campanas.map((c: Campana) => (
            <div key={c.id} className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-black text-slate-900">{c.nombre}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-500">
                      <Mail size={11} /> {CANALES[c.canal] ?? c.canal}
                    </span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${ESTADO_CLS[c.estado]}`}>
                      {c.estado}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (confirm(`Eliminar campaña "${c.nombre}"?`)) deleteMut.mutate(c.id);
                  }}
                  className="text-slate-300 hover:text-rose-600"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              {/* A/B test info */}
              {c.asunto_b && (
                <div className="mt-2 rounded-lg bg-violet-50 border border-violet-200 px-2.5 py-1.5 text-xs">
                  <span className="font-bold text-violet-700">A/B test</span>
                  {c.ganador_ab ? (
                    <span className="text-violet-600"> · ganador: variante {c.ganador_ab}</span>
                  ) : (
                    <button
                      onClick={() => abMut.mutate(c.id)}
                      className="text-violet-700 font-bold hover:underline ml-1"
                    >
                      · calcular ganador
                    </button>
                  )}
                </div>
              )}

              {c.metricas && (
                <div className="mt-4 space-y-2">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-lg bg-slate-50 py-2">
                      <div className="text-lg font-black text-slate-900">{c.metricas.enviados}</div>
                      <div className="text-[10px] uppercase text-slate-500 font-bold">Enviados</div>
                    </div>
                    <div className="rounded-lg bg-slate-50 py-2">
                      <div className="text-lg font-black text-slate-900">{c.metricas.tasa_apertura}%</div>
                      <div className="text-[10px] uppercase text-slate-500 font-bold">Apertura</div>
                    </div>
                    <div className="rounded-lg bg-slate-50 py-2">
                      <div className="text-lg font-black text-slate-900">{c.metricas.tasa_clic}%</div>
                      <div className="text-[10px] uppercase text-slate-500 font-bold">Clics</div>
                    </div>
                  </div>
                  <div className="rounded-xl bg-gradient-to-br from-brand-700 to-emerald-600 text-white p-3 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase font-bold text-emerald-100">
                        Ingresos atribuidos
                      </div>
                      <div className="text-lg font-black">{eur(c.metricas.ingresos_atribuidos, true)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase font-bold text-emerald-100">ROI</div>
                      <div className="text-lg font-black">
                        {c.metricas.roi !== null ? `${c.metricas.roi}%` : "—"}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500">
                    Presupuesto: {eur(c.metricas.presupuesto)}
                  </div>
                </div>
              )}

              {/* Botón enviar */}
              <button
                onClick={() => enviar(c.id, c.nombre)}
                disabled={!c.lista_id || enviando === c.id}
                title={!c.lista_id ? "Asigna una lista de destinatarios" : "Enviar a la lista"}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 disabled:opacity-40 text-white px-3 py-2 text-sm font-bold transition"
              >
                {enviando === c.id ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                {c.estado === "enviada" ? "Reenviar" : "Enviar campaña"}
              </button>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-slate-900">Nueva campaña</h2>
              <button type="button" onClick={() => setCreating(false)} className="text-slate-400 hover:text-slate-700">
                <X size={20} />
              </button>
            </header>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nombre *</label>
                <input
                  required
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Canal</label>
                  <select
                    value={form.canal}
                    onChange={(e) => setForm({ ...form, canal: e.target.value })}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    {Object.entries(CANALES).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Presupuesto €</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.presupuesto || ""}
                    onChange={(e) => setForm({ ...form, presupuesto: Number(e.target.value) })}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Objetivo</label>
                <input
                  type="text"
                  value={form.objetivo ?? ""}
                  onChange={(e) => setForm({ ...form, objetivo: e.target.value })}
                  placeholder="captación, fidelización…"
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Inicio</label>
                  <input
                    type="date"
                    value={form.fecha_inicio ?? ""}
                    onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Fin</label>
                  <input
                    type="date"
                    value={form.fecha_fin ?? ""}
                    onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Lista destinatarios</label>
                  <select
                    value={form.lista_id ?? ""}
                    onChange={(e) => setForm({ ...form, lista_id: e.target.value ? Number(e.target.value) : null })}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">— elegir lista —</option>
                    {listas?.map((l) => <option key={l.id} value={l.id}>{l.nombre} ({l.contactos_count ?? 0})</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Plantilla email</label>
                  <select
                    value={form.plantilla_id ?? ""}
                    onChange={(e) => setForm({ ...form, plantilla_id: e.target.value ? Number(e.target.value) : null })}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">— elegir plantilla —</option>
                    {plantillas?.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                </div>
              </div>
              <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-3">
                <div className="text-xs font-black uppercase tracking-wider text-violet-700 mb-2">
                  A/B test de asunto (opcional)
                </div>
                <input
                  type="text"
                  value={form.asunto_a ?? ""}
                  onChange={(e) => setForm({ ...form, asunto_a: e.target.value })}
                  placeholder="Asunto A"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mb-2"
                />
                <input
                  type="text"
                  value={form.asunto_b ?? ""}
                  onChange={(e) => setForm({ ...form, asunto_b: e.target.value })}
                  placeholder="Asunto B (déjalo vacío para no hacer A/B)"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold hover:bg-slate-50">
                Cancelar
              </button>
              <button type="submit" disabled={createMut.isPending} className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50">
                <Save size={14} /> Crear
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function PlantillasTab({ emisorId }: { emisorId: number | null }) {
  const { data: plantillas, isLoading } = usePlantillas(emisorId);
  const createMut = useCreatePlantilla();
  const deleteMut = useDeletePlantilla();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ nombre: "", asunto: "", html: "" });

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!emisorId) return;
    await createMut.mutateAsync({ emisorId, payload: form });
    setCreating(false);
    setForm({ nombre: "", asunto: "", html: "" });
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setCreating(true)} disabled={!emisorId} className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 font-bold text-sm shadow-soft disabled:opacity-50">
          <Plus size={16} /> Nueva plantilla
        </button>
      </div>
      {isLoading && <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando…</div>}
      {plantillas && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plantillas.length === 0 && <div className="col-span-full text-center text-slate-500 py-10">Sin plantillas.</div>}
          {plantillas.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-black text-slate-900">{p.nombre}</div>
                  <div className="text-sm text-slate-500 mt-1">{p.asunto}</div>
                </div>
                <button onClick={() => { if (confirm("¿Eliminar plantilla?")) deleteMut.mutate(p.id); }} className="text-slate-300 hover:text-rose-600">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6">
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-slate-900">Nueva plantilla</h2>
              <button type="button" onClick={() => setCreating(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </header>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nombre *</label>
                <input required type="text" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Asunto *</label>
                <input required type="text" value={form.asunto} onChange={(e) => setForm({ ...form, asunto: e.target.value })} placeholder="Usa {{nombre}} para personalizar" className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Cuerpo (HTML)</label>
                <textarea rows={8} value={form.html} onChange={(e) => setForm({ ...form, html: e.target.value })} className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono" placeholder="<p>Hola {{nombre}},</p>" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold hover:bg-slate-50">Cancelar</button>
              <button type="submit" disabled={createMut.isPending} className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"><Save size={14} /> Crear</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function ListasTab({ emisorId }: { emisorId: number | null }) {
  const { data: listas, isLoading } = useListas(emisorId);
  const createMut = useCreateLista();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ nombre: "", descripcion: "" });

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!emisorId) return;
    await createMut.mutateAsync({ emisorId, payload: form });
    setCreating(false);
    setForm({ nombre: "", descripcion: "" });
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setCreating(true)} disabled={!emisorId} className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 font-bold text-sm shadow-soft disabled:opacity-50">
          <Plus size={16} /> Nueva lista
        </button>
      </div>
      {isLoading && <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando…</div>}
      {listas && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {listas.length === 0 && <div className="col-span-full text-center text-slate-500 py-10">Sin listas.</div>}
          {listas.map((l) => (
            <div key={l.id} className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
              <div className="font-black text-slate-900">{l.nombre}</div>
              {l.descripcion && <div className="text-sm text-slate-500 mt-1">{l.descripcion}</div>}
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-slate-600 bg-slate-50 rounded-full px-3 py-1">
                <List size={12} /> {l.contactos_count ?? 0} contactos
              </div>
            </div>
          ))}
        </div>
      )}
      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-slate-900">Nueva lista</h2>
              <button type="button" onClick={() => setCreating(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </header>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nombre *</label>
                <input required type="text" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Descripción</label>
                <input type="text" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold hover:bg-slate-50">Cancelar</button>
              <button type="submit" disabled={createMut.isPending} className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"><Save size={14} /> Crear</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
